<script setup>
import { computed, ref, watch } from 'vue';
import { TTL_RANGES } from 'dashboard/helper/whatsappTemplateButtons';

// Período de validade da mensagem (message_send_ttl_seconds): se não for entregue nesse
// prazo, a Meta descarta a mensagem e não cobra.
const props = defineProps({
  category: { type: String, default: 'MARKETING' },
});
const seconds = defineModel({ type: [Number, String, null], default: null });

const UNITS = [
  { value: 60, label: 'minutos' },
  { value: 3600, label: 'horas' },
  { value: 86400, label: 'dias' },
];
const range = computed(() => TTL_RANGES[props.category] || null);
const enabled = ref(Boolean(seconds.value));
const unit = ref(86400);
const amount = ref(1);
const fromSeconds = value => {
  const best =
    [...UNITS].reverse().find(item => value % item.value === 0) || UNITS[0];
  unit.value = best.value;
  amount.value = Math.max(1, Math.round(value / best.value));
};
if (seconds.value) fromSeconds(Number(seconds.value));
else if (range.value) fromSeconds(range.value.default);

const total = computed(() => Math.round(Number(amount.value) * unit.value));
watch([enabled, total], () => {
  seconds.value = enabled.value && range.value ? total.value : null;
});
watch(range, value => {
  if (!value) enabled.value = false;
  else if (
    enabled.value &&
    (total.value < value.min || total.value > value.max)
  )
    fromSeconds(value.default);
});
const label = value => {
  if (value >= 86400 && value % 86400 === 0) return `${value / 86400} dia(s)`;
  if (value >= 3600 && value % 3600 === 0) return `${value / 3600} hora(s)`;
  if (value >= 60 && value % 60 === 0) return `${value / 60} minuto(s)`;
  return `${value} segundos`;
};
const outOfRange = computed(
  () =>
    enabled.value &&
    range.value &&
    (total.value < range.value.min || total.value > range.value.max)
);
const INPUT =
  'rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand';
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <section class="grid gap-2 rounded-xl border border-n-weak bg-n-alpha-1 p-4">
    <div class="flex items-start justify-between gap-3">
      <div>
        <h4 class="m-0 text-sm font-semibold text-n-slate-12">
          Período de validade da mensagem
        </h4>
        <p class="mb-0 mt-1 text-xs text-n-slate-11">
          Se a mensagem não for entregue dentro desse período, ela expira: o
          cliente não a vê e não há cobrança.
          {{
            enabled
              ? ''
              : 'Sem período personalizado, vale o padrão da Meta para a categoria.'
          }}
        </p>
      </div>
      <label class="inline-flex shrink-0 items-center gap-1.5 text-sm">
        <input v-model="enabled" type="checkbox" :disabled="!range" />
        Personalizar
      </label>
    </div>
    <div v-if="enabled && range" class="flex flex-wrap items-center gap-2">
      <input
        v-model.number="amount"
        type="number"
        min="1"
        :class="INPUT"
        class="w-24"
        aria-label="Período de validade"
      />
      <select v-model.number="unit" :class="INPUT" aria-label="Unidade">
        <option v-for="item in UNITS" :key="item.value" :value="item.value">
          {{ item.label }}
        </option>
      </select>
      <span class="text-xs text-n-slate-10">
        {{ `Entre ${label(range.min)} e ${label(range.max)}.` }}
      </span>
    </div>
    <p v-if="outOfRange" role="alert" class="m-0 text-xs text-n-ruby-11">
      Ajuste o período para ficar dentro da faixa permitida.
    </p>
    <p
      v-if="enabled && category === 'MARKETING'"
      class="m-0 text-xs text-n-amber-11"
    >
      Em marketing, a Meta só aplica o período personalizado em contas
      habilitadas para a API de mensagens de marketing; se não for aceito, o
      erro da Meta aparece ao salvar.
    </p>
  </section>
</template>
